import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  ShoppingCart, 
  Package, 
  DollarSign, 
  TrendingDown,
  ArrowLeft,
  Loader2,
  Sparkles,
  Info,
  Calendar,
  User,
  CreditCard,
  Banknote,
  ExternalLink,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to normalize image URLs
const normalizeImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('https://images.unsplash.com')) return url;
  if (url.startsWith('/api/')) return `${API_BASE_URL}${url}`;
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/.+$/);
    if (match) return `${API_BASE_URL}${match[0]}`;
  }
  return url;
};

const CATEGORY_IMAGES = {
  electronics: 'https://images.unsplash.com/photo-1750797308931-b0d261abb3d5?w=400&h=300&fit=crop',
  furniture: 'https://images.unsplash.com/photo-1673695008224-fa5a9d195dfb?w=400&h=300&fit=crop',
  clothing: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&h=300&fit=crop',
  vehicles: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop',
  sports: 'https://images.unsplash.com/photo-1461896836934-fffbd4f9b0db?w=400&h=300&fit=crop',
  home: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop',
  collectibles: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
  books: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=400&h=300&fit=crop',
  tools: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=400&h=300&fit=crop',
  other: 'https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=400&h=300&fit=crop',
};

export default function PurchaseHistoryPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ purchases: [], summary: {} });

  useEffect(() => {
    fetchPurchaseHistory();
  }, []);

  const fetchPurchaseHistory = async () => {
    try {
      const response = await api.get('/purchase-history');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch purchase history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleDownloadReceipt = async (transactionId) => {
    try {
      const response = await api.get(`/transactions/${transactionId}/receipt`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `WANTD-Receipt-${transactionId.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download receipt:', error);
    }
  };

  const { purchases, summary } = data;

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#C9956C]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center gap-2 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)] mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center">
              <ShoppingCart className="w-7 h-7 text-[#C9956C]" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-[var(--theme-text-primary)]">
                Purchase History
              </h1>
              <p className="text-[var(--theme-text-secondary)] mt-1">
                Track your WANTD purchases and savings
              </p>
            </div>
          </div>
        </div>

        {/* Savings Summary Banner */}
        <div className="mb-8 bg-gradient-to-r from-[#059669] to-[#047857] rounded-2xl p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5" />
              <span className="text-white/80 text-sm font-medium">Your WANTD Savings</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="text-4xl font-black mb-1">
                  ${summary.total_savings?.toLocaleString() || 0}
                </div>
                <div className="text-white/70 text-sm">
                  Total saved vs buying new
                </div>
              </div>
              <div>
                <div className="text-4xl font-black mb-1">
                  ${summary.total_spent?.toLocaleString() || 0}
                </div>
                <div className="text-white/70 text-sm">
                  Total spent on WANTD
                </div>
              </div>
              <div>
                <div className="text-4xl font-black mb-1">
                  {summary.total_items || 0}
                </div>
                <div className="text-white/70 text-sm">
                  Items purchased
                </div>
              </div>
            </div>

            {summary.total_rrp > 0 && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center gap-2 text-sm">
                  <TrendingDown className="w-4 h-4" />
                  <span>
                    You paid <strong>${summary.total_spent?.toLocaleString()}</strong> for items worth{' '}
                    <strong>${summary.total_rrp?.toLocaleString()}</strong> new
                    {summary.total_spent > 0 && summary.total_rrp > 0 && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full">
                        {Math.round((1 - summary.total_spent / summary.total_rrp) * 100)}% off RRP
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="flex items-start gap-2 mt-4 pt-4 border-t border-white/20">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-white/60" />
              <p className="text-xs text-white/60">
                RRP estimates are AI-generated based on the closest identified new equivalent. 
                Savings calculated from {summary.items_with_rrp_data || 0} of {summary.total_items || 0} purchases with RRP data.
              </p>
            </div>
          </div>
        </div>

        {/* Purchase List */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-[var(--theme-text-primary)] flex items-center gap-2">
            <Package className="w-5 h-5" />
            All Purchases
          </h2>

          {purchases.length === 0 ? (
            <div className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--theme-hover)] flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-8 h-8 text-[var(--theme-text-muted)]" />
              </div>
              <h3 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">No purchases yet</h3>
              <p className="text-[var(--theme-text-secondary)] mb-6 max-w-md mx-auto">
                When you complete transactions on WANTD, they will appear here along with your savings compared to buying new.
              </p>
              <Button 
                onClick={() => navigate('/dashboard/wanter')}
                className="bg-[#1E3A5F] hover:bg-[#2D4A6F] rounded-full"
              >
                Browse My Wants
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {purchases.map((purchase) => {
                const imageUrl = normalizeImageUrl(purchase.item_image) || 
                  CATEGORY_IMAGES[purchase.item_category] || 
                  CATEGORY_IMAGES.other;
                const savings = purchase.item_rrp ? purchase.item_rrp - purchase.amount : 0;
                const savingsPercent = purchase.item_rrp ? Math.round((1 - purchase.amount / purchase.item_rrp) * 100) : 0;

                return (
                  <div 
                    key={purchase.id}
                    className="bg-[var(--theme-card)] rounded-2xl border border-[var(--theme-border)] overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="md:w-48 h-48 md:h-auto flex-shrink-0">
                        <img 
                          src={imageUrl}
                          alt={purchase.item_title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-5">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-[var(--theme-text-primary)] mb-1">
                              {purchase.item_title || 'Item'}
                            </h3>
                            
                            <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--theme-text-secondary)] mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(purchase.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                from {purchase.seller_username || 'Seller'}
                              </span>
                              <span className="flex items-center gap-1">
                                {purchase.payment_method === 'stripe' ? (
                                  <CreditCard className="w-4 h-4" />
                                ) : (
                                  <Banknote className="w-4 h-4" />
                                )}
                                {purchase.payment_method === 'stripe' ? 'Card' : 'Cash'}
                              </span>
                            </div>

                            {purchase.item_condition && (
                              <span className="inline-block px-2 py-1 rounded text-xs bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] capitalize">
                                {purchase.item_condition.replace('_', ' ')}
                              </span>
                            )}
                          </div>

                          {/* Price & Savings */}
                          <div className="text-right">
                            <div className="text-2xl font-black text-[var(--theme-text-primary)]">
                              ${purchase.amount?.toLocaleString()}
                            </div>
                            <div className="text-xs text-[var(--theme-text-secondary)]">paid</div>
                            
                            {purchase.item_rrp && savings > 0 && (
                              <div className="mt-2 p-2 rounded-lg bg-[#059669]/10">
                                <div className="text-xs text-[var(--theme-text-muted)] line-through">
                                  ${purchase.item_rrp?.toLocaleString()} RRP
                                </div>
                                <div className="text-sm font-bold text-[#059669]">
                                  Saved ${savings.toLocaleString()} ({savingsPercent}% off)
                                </div>
                              </div>
                            )}

                            {!purchase.item_rrp && (
                              <div className="mt-2 text-xs text-[var(--theme-text-muted)]">
                                No RRP data
                              </div>
                            )}
                          </div>
                        </div>

                        {/* View Match Link + Receipt */}
                        <div className="mt-4 pt-4 border-t border-[var(--theme-border)] flex items-center justify-between">
                          <Link
                            to={`/matches/${purchase.match_id}`}
                            className="text-sm text-[#C9956C] hover:text-[#B8845B] flex items-center gap-1"
                          >
                            View transaction details
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                          {purchase.transaction_id && (
                            <button
                              onClick={() => handleDownloadReceipt(purchase.transaction_id)}
                              className="text-sm text-[var(--theme-text-secondary)] hover:text-[#1E3A5F] flex items-center gap-1 transition-colors"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Receipt
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
